import * as _ from 'lodash';
import {useMemo} from 'react';

import {
  Project,
  useCreateReportsPromptQuery,
} from './../../../generated/graphql';
import {useViewer} from './../../../state/viewer/hooks';

/**
 * When deciding whether or not to show the CreateReportPrompt we need to know a few things
 * 1. We need to know run information for the viewer, or for the entity
 * 2. We need to access the projects data for the entity
 * 3. We need to know the business rules for choosing a project to seed the prompt with
 *
 * We cannot link to a "Create report" page w/out having a project for the report to be created under. The current rules for determining what project a user should be prompted to create a report for are:
 * 1. Find projects with 5 or more runs
 * 2. If there is more than 1 qualifying project, use the project with the most recent run
 * 3. Do not link to associated projects (such as team projects for an individual entity), only projects associated with the direct entity
 */
export function useCreateReportPromptProjectData(
  entityName?: string
): null | Pick<Project, 'id' | 'name' | 'entityName'> {
  const viewerData = useViewer();

  const resolvedEntity = entityName || viewerData?.entity;

  const {data, error} = useCreateReportsPromptQuery({
    fetchPolicy: 'network-only',
    variables: {
      entityName: resolvedEntity,
      last: 150, // TODO: this currently doesn't work in GraphQL, investigate
    },
  });

  const runs = useMemo(() => {
    return (
      data?.viewer?.runs?.edges
        .map(e => e.node)
        .filter(run => run?.project?.entityName === viewerData?.entity) ?? []
    );
  }, [data?.viewer?.runs, viewerData?.entity]);

  const projectWithEnoughRuns = useMemo(() => {
    // remove the runs where the entity on the project is not the same entity as the viewer "in scope" - e.g. viewing a team

    // Grouping by entityName/projectName gets us the number of runs per project
    const runsByProject = _.groupBy(
      runs,
      run => `${run.project?.entityName}/${run.project?.name}`
    );

    // once they're grouped we can filter off non-qualifying runs in low-volume projects and then find the most recent run
    return (
      runs
        .filter(
          r =>
            runsByProject[`${r.project?.entityName}/${r.project?.name}`]
              .length > 4
        )
        .sort((a, b) => {
          const aDate = new Date(a.updatedAt ?? a.createdAt);
          const bDate = new Date(b.updatedAt ?? b.createdAt);

          return aDate > bDate ? -1 : 1;
        })[0]?.project ?? null
    );
  }, [runs]);

  if (error || !runs.length || resolvedEntity !== viewerData?.entity) {
    return null;
  }
  return projectWithEnoughRuns ?? null;
}
